/** TRANSLATION */
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import {
    Autocomplete,
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
    AutocompleteRenderInputParams,
    Box,
    Button,
    Checkbox,
    Paper,
    SxProps,
    TextField,
    Theme,
    Typography
} from '@mui/material'

/** ICON */
import {
    faCaretDown,
    faCheck
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/** CONSTANTS */
import { colors } from 'constants/theme'

/** STYLE */
import { styled } from '@mui/material/styles'
import HelperText from 'component/Input/HelperText'
import 'component/Select/Style.css'


const CustomAutocomplete = styled(Autocomplete)(({ theme }) => ({
    fontFamily: 'Kanit',
    // '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
    // },
    '.MuiOutlinedInput-root': {
        backgroundColor: colors.white,
        // padding: '8px 8px 8px 8px',
        height: 43,
        alignItems: 'center !important',
        '.MuiButtonBase-root': {
            '&.MuiIconButton-root': {
                backgroundColor: `${colors.transparent} !important`,
            },
            svg: {
                fontSize: 16,
                path: {
                    fill: colors.black
                }
            }
        },
        '&.Mui-disabled': {
            backgroundColor: `#E0E0E0 !important`,
            '.MuiButtonBase-root svg': {
                path: {
                    fill: colors.black60
                }
            },
        },
        '&.MuiInputBase-sizeSmall': {
            '.MuiAutocomplete-input': {
                paddingRight: '16px'
            }
        },
        '&.Mui-focused': {
            borderColor: `${colors.themeSecondColor}`,
            border: `1px solid ${colors.themeSecondColor} !important`,
            '.MuiOutlinedInput-notchedOutline': {
                border: 'none'
                // borderColor: `${colors.themeSecondColor}`,
            }
        },
        '.MuiOutlinedInput-notchedOutline': {
            // borderWidth: '1px !important'
        }
    },
    '.MuiInputLabel-root, .MuiInputLabel-root span': {
        fontSize: 16,
        color: `${colors.black60} !important`,
        backgroundColor: 'transparent !important',
        '&:before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: 3,
            top: '50%',
            zIndex: -1
        },
        '&[data-shrink="true"]': {
            '&:before': {
                backgroundColor: colors.white
            },
            span: {
                color: `${colors.statusInactive} !important`
            }
        }
    },
    '.MuiOutlinedInput-notchedOutline': {
        // borderColor: `${colors.black12} !important`
    },
    '.MuiOutlinedInput-input': {
        padding: '8px 8px 8px 8px',
        textIndent: '1px',
        color: `${colors.textPrimary} !important`,
        '-webkit-text-fill-color': 'unset !important',
        '&.Mui-disabled': {
            '-webkit-text-fill-color': 'unset',
            color: `${colors.black60} !important`,
            '&::placeholder': {
                opacity: 1
            }
        }
    },
    '.MuiAutocomplete-clearIndicator': {
        backgroundColor: 'transparent !important',
        svg: {
            fontSize: 20
        },
        path: {
            fill: `${colors.black60} !important`
        }
    },
    'span.MuiAutocomplete-tag': {
        height: 23,
        minWidth: 36,
        backgroundColor: colors.disabledGray,
        borderRadius: 100,
        color: colors.white,
        textAlign: 'center'
    },
    '&.Mui-enabled-border': {
        '.MuiOutlinedInput-root': {
            '&.Mui-focused': {
                // '.MuiOutlinedInput-notchedOutline': {
                // borderColor: `${colors.themeSecondColor} !important`
                // }
            }
        },
        '.MuiInputLabel-root, .MuiInputLabel-root span': {
            '&[data-shrink="true"]': {
                '&.Mui-focused': {
                    color: `${colors.themeSecondColor} !important`
                }
            }
        }
    },

    '&.Mui-error': {
        '&.Mui-enabled-border, &.Mui-disabled-border, .MuiOutlinedInput-root.Mui-focused': {
            '.MuiOutlinedInput-notchedOutline': {
                // border: `1px solid ${colors.statusInactive} !important`
            },
            '.MuiInputLabel-root[data-shrink="true"], .MuiInputLabel-root[data-shrink="true"] span': {
                color: `${colors.statusInactive} !important`
            }
        }
    },
    '.MuiInputBase-inputMultiline': {
        height: '90% !important'
    },
    '&.Mui-multiple': {
        '.MuiOutlinedInput-root': {
            paddingTop: '10px !important'
        },
        '.MuiAutocomplete-clearIndicator': {
            marginRight: '0.375rem'
        }
    },
    '& .MuiAutocomplete-noOptions': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        padding: theme.spacing(1, 0)
    }
}))

const HighlightedText = styled('span')<{ highlight?: boolean }>(({ highlight }) => ({
    backgroundColor: highlight ? '#fff3cd' : 'transparent',
    fontWeight: highlight ? 600 : 400,
    color: highlight ? '#856404' : 'inherit',
    padding: highlight ? '2px 4px' : 0,
    borderRadius: highlight ? 4 : 0,
}))

const SelectAllOption = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#f8f9fa',
    color: colors.black,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#e9ecef',
    }
}))

const OptionItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#f8f9fa',
    }
}))

interface ExcelDataRow {
    id: string | number
    [key: string]: any
}

interface SearchFilter {
    column: string
    value: string
    operator: 'contains' | 'equals' | 'startsWith' | 'endsWith'
}

interface GoogleExcelSelectSearchProps {
    labelId: string
    data: ExcelDataRow[]
    columns: string[]
    displayColumns?: string[]
    searchColumns?: string[]
    placeholder?: string
    inputLabel?: string
    size?: 'medium' | 'small'
    multiSelect?: boolean
    selectedRows?: ExcelDataRow[]
    value?: ExcelDataRow | ExcelDataRow[] | null
    defaultValue?: any
    maxResults?: number
    loading?: boolean
    disabled?: boolean
    required?: boolean
    errMessage?: string
    noOptionsText?: string
    disableClearable?: boolean
    height?: number | string
    limitTags?: number
    showSelectionButtons?: boolean
    selectAllText?: string
    clearAllText?: string
    getOptionLabel?: (option: ExcelDataRow) => string
    getOptionDisabled?: (option: ExcelDataRow) => boolean
    renderOption?: (props: any, option: ExcelDataRow, inputValue: string) => React.ReactNode
    onRowSelect?: (row: ExcelDataRow) => void
    onSelectionChange?: (selectedRows: ExcelDataRow[]) => void
    onSelectAll?: () => void
    onClearAll?: () => void
    onChange?: (event: any, value: any, reason: AutocompleteChangeReason) => void
    onInputChange?: (event: any, value: any, reason?: AutocompleteInputChangeReason) => void
    onOpen?: (event: any) => void
    onClose?: (event: any, reason: any) => void
    onBlur?: (event: any) => void
    sx?: SxProps<Theme>
}

export default function GoogleExcelSelectSearch(props: GoogleExcelSelectSearchProps) {
    const { t } = useTranslation()
    const [inputValue, setInputValue] = useState('')
    const [filters, setFilters] = useState<SearchFilter[]>([])
    const [internalSelectedRows, setInternalSelectedRows] = useState<ExcelDataRow[]>([])
    const [open, setOpen] = useState(false)

    const {
        data = [],
        columns = [],
        displayColumns = columns.slice(0, 2),
        searchColumns = columns,
        multiSelect = false,
        selectedRows,
        maxResults = 100,
        loading = false,
        disabled = false,
        placeholder = 'ค้นหาและเลือกข้อมูล...',
        inputLabel,
        size = 'small',
        showSelectionButtons = true,
        selectAllText,
        clearAllText,
        getOptionLabel = (option: ExcelDataRow) => String(option[displayColumns[0]] || ''),
        getOptionDisabled = () => false,
        renderOption: customRenderOption,
        onChange,
        onInputChange,
        onRowSelect,
        onSelectionChange,
        onSelectAll,
        onClearAll,
        ...autocompleteProps
    } = props

    // Use controlled or uncontrolled selected rows
    let currentSelectedRows = internalSelectedRows
    if (selectedRows !== undefined) {
        currentSelectedRows = selectedRows
    }

    // Ensure currentSelectedRows is always an array
    if (!Array.isArray(currentSelectedRows)) {
        currentSelectedRows = []
    }

    // Normalize text for search
    const normalizeText = (text: string) =>
        (text ?? '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()

    // Filter and search data with Google Excel style filtering
    const filteredData = useMemo(() => {
        if (!data?.length) return []

        let filtered = data.slice()

        // Apply search term
        if (inputValue.trim()) {
            const normalizedSearch = normalizeText(inputValue)
            const searchTokens = normalizedSearch.split(/\s+/).filter(Boolean)

            filtered = filtered?.filter(row => {
                const searchableText = searchColumns
                    .map(col => normalizeText(String(row[col] || '')))
                    .join(' ')

                return searchTokens.every(token => searchableText.includes(token))
            })
        }

        // Apply filters
        filters.forEach(filter => {
            filtered = filtered.filter(row => {
                const cellValue = normalizeText(String(row[filter.column] || ''))
                const filterValue = normalizeText(filter.value)

                switch (filter.operator) {
                    case 'equals':
                        return cellValue === filterValue
                    case 'startsWith':
                        return cellValue.startsWith(filterValue)
                    case 'endsWith':
                        return cellValue.endsWith(filterValue)
                    case 'contains':
                    default:
                        return cellValue.includes(filterValue)
                }
            })
        })

        return filtered.slice(0, maxResults)
    }, [data, inputValue, filters, searchColumns, maxResults])

    // Add SELECT_ALL option for multi-select
    const SELECT_ALL_ID = '__select_all__'
    const optionsWithSelectAll = useMemo(() => {
        if (!multiSelect) return filteredData

        const selectAllOption: ExcelDataRow = {
            id: SELECT_ALL_ID,
            [displayColumns[0]]: `เลือกทั้งหมด (${filteredData?.length || 0} รายการ)`,
            __isSelectAll: true
        }

        return [selectAllOption, ...filteredData]
    }, [multiSelect, filteredData, displayColumns])

    // Check if row is selected
    const isRowSelected = useCallback((row: ExcelDataRow) => {
        return Array.isArray(currentSelectedRows) && currentSelectedRows.some(selectedRow => selectedRow.id === row.id)
    }, [currentSelectedRows])

    // Handle selection
    const handleSelectionChange = useCallback((row: ExcelDataRow) => {
        if (multiSelect) {
            let newSelectedRows: ExcelDataRow[]

            if (row.id === SELECT_ALL_ID) {
                // Handle select all
                const allSelected = filteredData?.length > 0 &&
                    filteredData.every(r => isRowSelected(r))

                if (allSelected) {
                    // Unselect all visible rows
                    newSelectedRows = currentSelectedRows?.filter(selectedRow =>
                        !filteredData.some(visibleRow => visibleRow.id === selectedRow.id)
                    ) || []
                } else {
                    // Select all visible rows
                    const rowsToAdd = filteredData?.filter(r => !isRowSelected(r)) || []
                    newSelectedRows = [...(currentSelectedRows || []), ...rowsToAdd]
                }
            } else {
                // Handle individual row
                if (isRowSelected(row)) {
                    newSelectedRows = currentSelectedRows?.filter(selectedRow => selectedRow.id !== row.id) || []
                } else {
                    newSelectedRows = [...(currentSelectedRows || []), row]
                }
            }

            // Update state
            if (selectedRows === undefined) {
                setInternalSelectedRows(newSelectedRows)
            }

            onSelectionChange?.(newSelectedRows)
        } else {
            onRowSelect?.(row)
        }
    }, [multiSelect, currentSelectedRows, isRowSelected, filteredData, selectedRows, onSelectionChange, onRowSelect, SELECT_ALL_ID])

    // Handle external select all button
    const handleSelectAllButton = useCallback((e?: any) => {
        // prevent the click from closing the dropdown
        e?.stopPropagation()
        e?.preventDefault()
        if (onSelectAll) {
            onSelectAll()
        } else {
            // Default behavior
            const rowsToAdd = filteredData?.filter(r => !isRowSelected(r)) || []
            const newSelectedRows = [...(currentSelectedRows || []), ...rowsToAdd]

            if (selectedRows === undefined) {
                setInternalSelectedRows(newSelectedRows)
            }

            onSelectionChange?.(newSelectedRows)
        }
        // keep dropdown open
        setOpen(true)
    }, [onSelectAll, filteredData, isRowSelected, currentSelectedRows, selectedRows, onSelectionChange])

    // Handle external clear all button
    const handleClearAllButton = useCallback((e?: any) => {
        e?.stopPropagation()
        e?.preventDefault()

        if (onClearAll) {
            onClearAll()
        } else {
            // Default behavior
            const newSelectedRows: ExcelDataRow[] = []

            if (selectedRows === undefined) {
                setInternalSelectedRows(newSelectedRows)
            }

            onSelectionChange?.(newSelectedRows)
        }
        // keep dropdown open
        setOpen(true)
    }, [onClearAll, selectedRows, onSelectionChange])

    // Highlight matching text
    const highlightText = useCallback((text: string, search: string) => {
        if (!search.trim()) return text

        const normalizedText = text.toString()
        const normalizedSearch = normalizeText(search)
        const searchTokens = normalizedSearch.split(/\s+/).filter(Boolean)

        let result = normalizedText
        searchTokens.forEach(token => {
            if (token?.length > 1) {
                const regex = new RegExp(`(${token})`, 'gi')
                result = result.replace(regex, (match) => `<mark>${match}</mark>`)
            }
        })

        return result.split(/(<mark>.*?<\/mark>)/).map((part, index) => {
            if (part.startsWith('<mark>') && part.endsWith('</mark>')) {
                return (
                    <HighlightedText key={index} highlight>
                        {part.slice(6, -7)}
                    </HighlightedText>
                )
            }
            return part
        })
    }, [])

    // Get select all state
    const getSelectAllState = useCallback(() => {
        if (!Array.isArray(filteredData) || filteredData.length === 0) return 'none'

        const selectedVisibleCount = filteredData.filter(row => isRowSelected(row)).length

        if (selectedVisibleCount === 0) return 'none'
        if (selectedVisibleCount === filteredData.length) return 'all'
        return 'some'
    }, [filteredData, isRowSelected])

    // Custom render option
    const defaultRenderOption = useCallback((props: any, option: ExcelDataRow) => {
        const isSelectAll = option.id === SELECT_ALL_ID

        if (isSelectAll) {
            const selectAllState = getSelectAllState()
            return (
                <SelectAllOption {...props} key={SELECT_ALL_ID}>
                    <Checkbox
                        checked={selectAllState === 'all'}
                        indeterminate={selectAllState === 'some'}
                        sx={{ mr: 1 }}
                        size="small"
                    />
                    {/* <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 8, color: '#1976d2' }} /> */}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getOptionLabel(option)}
                    </Typography>
                </SelectAllOption>
            )
        }

        const isSelected = isRowSelected(option)
        return (
            <OptionItem {...props} key={option.id}>
                {multiSelect && (
                    <Checkbox
                        checked={isSelected}
                        sx={{ mr: 1 }}
                        size="small"
                    />
                )}
                <Box flex={1}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {highlightText(getOptionLabel(option), inputValue)}
                    </Typography>
                    {displayColumns.length > 1 && (
                        <Typography variant="caption" color="text.secondary">
                            {displayColumns.slice(1, 3).map(col =>
                                String(option[col] || '')
                            ).join(' • ')}
                        </Typography>
                    )}
                </Box>
                {!multiSelect && isSelected && (
                    <FontAwesomeIcon icon={faCheck} style={{ color: '#4caf50', fontSize: 14 }} />
                )}
            </OptionItem>
        )
    }, [multiSelect, isRowSelected, getOptionLabel, highlightText, inputValue, displayColumns, getSelectAllState, SELECT_ALL_ID])

    const renderOption = customRenderOption || defaultRenderOption

    // Handle Autocomplete onChange
    const handleAutocompleteChange = useCallback((
        event: any,
        newValue: ExcelDataRow | ExcelDataRow[] | null,
        reason: AutocompleteChangeReason,
        details?: any
    ) => {
        const clickedOption = details?.option

        if (clickedOption) {
            handleSelectionChange(clickedOption)
        }

        onChange?.(event, newValue, reason)
    }, [handleSelectionChange, onChange])

    // Handle input change
    const handleInputChange = useCallback((
        event: any,
        value: string,
        reason?: AutocompleteInputChangeReason
    ) => {
        setInputValue(value)
        onInputChange?.(event, value, reason)
    }, [onInputChange])

    // Get current value for Autocomplete
    const autocompleteValue = useMemo(() => {
        if (multiSelect) {
            return Array.isArray(currentSelectedRows) ? currentSelectedRows : []
        }
        return Array.isArray(currentSelectedRows) && currentSelectedRows.length > 0 ? currentSelectedRows[0] : null
    }, [multiSelect, currentSelectedRows])

    const hasValue = multiSelect
        ? Array.isArray(currentSelectedRows) && currentSelectedRows.length > 0
        : Array.isArray(currentSelectedRows) && currentSelectedRows.length > 0

    return (
        <>
            {/* Selection Buttons are rendered inside the dropdown PaperComponent */}

            <CustomAutocomplete
                {...autocompleteProps}
                id={props.labelId}
                options={optionsWithSelectAll}
                value={autocompleteValue}
                inputValue={inputValue}
                onChange={handleAutocompleteChange as any}
                onInputChange={handleInputChange}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                getOptionLabel={getOptionLabel as any}
                getOptionDisabled={getOptionDisabled as any}
                renderOption={renderOption as any}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={inputLabel}
                        placeholder={placeholder}
                        required={props.required}
                    />
                )}
                size={size}
                multiple={multiSelect}
                disabled={disabled}
                loading={loading}
                loadingText={t('SEARCHING')}
                noOptionsText={props.noOptionsText || t('REGISTER.FORM.NOT_FOUND')}
                disableClearable={props.disableClearable || !hasValue}
                limitTags={props.limitTags}
                popupIcon={<FontAwesomeIcon icon={faCaretDown} />}
                // clearIcon={<FontAwesomeIcon icon={faTimes} />}
                filterOptions={(options) => options} // ใช้ filter ของเราเอง
                disableCloseOnSelect={multiSelect}
                className={`Mui-enabled-border ${props.errMessage ? 'Mui-error' : ''} ${multiSelect ? 'Mui-multiple' : ''}`}
                classes={{
                    popper: 'style-filter-select-search-wrap',
                    paper: 'style-filter-select-search',
                    listbox: multiSelect ? 'Mui-multiple custom-scroll' : 'custom-scroll'
                }}
                sx={(theme: Theme) => ({
                    // Responsive input root styling - fixed border
                    '.MuiOutlinedInput-root': {
                        padding: '0 !important', // Remove default padding
                        minHeight: props.height || 43,
                        height: multiSelect ? 'auto' : (props.height || 43),
                        alignItems: 'center !important',
                        maxHeight: multiSelect ? '60px' : 'auto',
                        display: 'flex',
                        overflow: "auto", // Hide overflow on the border container
                        // position: 'relative',

                        // Responsive adjustments
                        '@media (max-width:899.95px)': {
                            minHeight: 40,
                        },
                        '@media (max-width:599.95px)': {
                            minHeight: 36,
                            fontSize: '14px',
                        },
                    },

                    // Input field responsive
                    '.MuiAutocomplete-input': {
                        flex: '1 1 auto',
                        padding: '8px !important',
                        margin: '0',
                        display: 'flex',
                        overflow: "auto", // Hide overflow on the border container
                        // position: 'relative'
                        '@media (max-width:599.95px)': {
                            fontSize: '14px !important',
                        }
                    },

                    // End adornment (dropdown arrow) responsive
                    '.MuiAutocomplete-endAdornment': {
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,

                        '@media (max-width:899.95px)': {
                            right: '6px',
                        },
                        '@media (max-width:599.95px)': {
                            right: '4px',
                            '& .MuiSvgIcon-root, & svg': {
                                fontSize: '18px',
                            }
                        }
                    },

                    // Input base responsive
                    '.MuiInputBase-input': {
                        padding: '0 8px !important',

                        '@media (max-width:599.95px)': {
                            fontSize: '14px !important',
                        }
                    },

                    // Label responsive
                    '.MuiInputLabel-root': {
                        '@media (max-width:599.95px)': {
                            fontSize: '14px !important',
                        }
                    },

                    ...(typeof props.sx === 'function' ? (props.sx as any)(theme) : (props.sx as any))
                }) as any}
                PaperComponent={({ children }) => (
                    <Paper sx={{
                        // Responsive dropdown width
                        minWidth: '280px',
                        maxWidth: '90vw',

                        '@media (max-width:899.95px)': {
                            minWidth: '260px',
                        },
                        '@media (max-width:599.95px)': {
                            minWidth: '240px',
                            maxWidth: '95vw',
                        },

                        '.MuiAutocomplete-option': {
                            padding: '8px 12px',

                            '&:hover, &:hover.Mui-focused': {
                                backgroundColor: `${colors.themeSecondColor06} !important`,
                                color: colors.themeSecondColor
                            },
                            '&[aria-selected="true"], &.Mui-focused': {
                                backgroundColor: `${colors.transparent} !important`,
                            },

                            // Responsive option padding
                            '@media (max-width:599.95px)': {
                                padding: '6px 10px',
                                fontSize: '14px',
                            }
                        }
                    }}>
                        {multiSelect && showSelectionButtons && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                borderBottom: '1px solid #eee',
                                flexWrap: 'wrap',
                                gap: 1,

                                // Responsive button container
                                '@media (max-width:599.95px)': {
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    gap: 0.5,
                                }
                            }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        '@media (max-width:599.95px)': {
                                            fontSize: '11px',
                                            textAlign: 'center',
                                        }
                                    }}
                                >
                                    {Array.isArray(currentSelectedRows) && currentSelectedRows.length > 0 ? `เลือกแล้ว ${currentSelectedRows.length} รายการ` : ''}
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 1,

                                    '@media (max-width:599.95px)': {
                                        width: '100%',
                                        gap: 0.5,
                                    }
                                }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onClick={(e) => handleSelectAllButton(e)}
                                        disabled={disabled || loading || !Array.isArray(filteredData) || filteredData.length === 0}
                                        sx={{
                                            minWidth: 96,
                                            px: 1,

                                            '@media (max-width:599.95px)': {
                                                minWidth: 'auto',
                                                flex: 1,
                                                fontSize: '12px',
                                                py: 0.5,
                                            }
                                        }}
                                    >
                                        {selectAllText || 'เลือกทั้งหมด'}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onClick={(e) => handleClearAllButton(e)}
                                        disabled={disabled || loading || !Array.isArray(currentSelectedRows) || currentSelectedRows.length === 0}
                                        sx={{
                                            minWidth: 96,
                                            px: 1,

                                            '@media (max-width:599.95px)': {
                                                minWidth: 'auto',
                                                flex: 1,
                                                fontSize: '12px',
                                                py: 0.5,
                                            }
                                        }}
                                    >
                                        {clearAllText || 'ล้างทั้งหมด'}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        {children}
                    </Paper>
                )}
            />
            {props.errMessage && (
                <HelperText label={props.errMessage} />
            )}
        </>
    )
}
